import { useMemo, useState } from 'react'
import { Card, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatMoneyValue } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PageHeader } from '@/shared/ui/PageHeader'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { DataTable } from '@/shared/ui/table/DataTable'
import { OrgNodeTreeSelect } from '@/features/hr/OrgNodeTreeSelect'
import { useEmployeeNodes } from '@/features/hr/useEmployeeNodes'
import { fetchSalaryPage, type PayItem } from '@/features/hr/payApi'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'

/**
 * Tashkilot bo'ylab amaldagi maoshlar — «kim qancha oladi».
 *
 * <h2>⚠️ HUQUQSIZ SO'ROV UMUMAN YUBORILMAYDI</h2>
 * `hr.salary.view` hech bir tizim roliga o'z-o'zidan tushmaydi
 * (`V202608150700`) va bu sahifa aynan shu huquq ortida turadi.
 * Huquq yo'q bo'lsa sahifa BIRORTA so'rov yubormaydi — `DataTable`
 * umuman chizilmaydi (erta `return`). Sabab `SalaryCard` dagi bilan
 * bir xil: 403 javobining O'ZI ham «bu tashkilotda maosh yozuvi bor»
 * degan faktni tasdiqlardi va u tarmoq jurnalida qolardi.
 *
 * <p>⚠️ Niqob (`***`) YO'Q: niqoblangan son «bu yerda summa bor» deb
 * turadi va bu ham ma'lumot.
 *
 * <h2>⚠️ MARSHRUTDA `RequirePermission` YO'Q — VA BU ATAYLAB</h2>
 * Qo'yilsa, pastdagi `PermissionNotice` shoxiga hech qachon
 * kirilmasdi (o'lik kod) va uni tekshiradigan qorovul YOLG'ON YASHIL
 * berardi. Ustiga umumiy «Ruxsat yo'q» sahifasi eng kerakli narsani
 * aytmaydi — QAYSI huquqni so'rash kerakligini.
 *
 * <h2>⚠️ BIR QATOR = BIR QISM, BIR XODIM EMAS</h2>
 * `GET /api/hr/salaries` `hr.pay_item` ning AMALDAGI satrlarini
 * qaytaradi. Okladi va ustamasi bor xodim ikki marta chiqadi.
 * Qatorlarni sanab «xodimlar soni» deb o'qish noto'g'ri bo'lardi,
 * shuning uchun buni izoh ochiq aytadi.
 *
 * <h2>⚠️ ISM JAVOBDA KELADI — IKKINCHI SO'ROV YO'Q</h2>
 * `PayItemView.fullName` (`payApi.ts` 4-qoidasi). Bu sahifa ilgari
 * ismni `GET /api/hr/persons` dan alohida olardi va o'sha yamoq
 * 2026-08-14 da OLIB TASHLANDI: ism bilan saralash tartibi endi BIR
 * manbadan keladi va ekranda «ism uchun `hr.person.read` kerak»
 * degan CHALG'ITUVCHI matn qolmadi.
 *
 * <p>⚠️ Shu sababdan bu sahifada to'g'ridan-to'g'ri `useQuery` YO'Q:
 * qolgan ikki so'rovni `useEmployeeNodes(canView)` va `DataTable`
 * yuboradi, ikkalasi ham `canView` ortida.
 *
 * <p>⚠️ Tabel raqami ko'rsatilmaydi — `PayItemView` da `personnelNo`
 * yo'q. Uni ikkinchi so'rov bilan to'ldirish yuqoridagi yamoqni
 * qaytarish bo'lardi.
 */
export function HrSalariesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canView = can('hr.salary.view')

  // ⚠️ N4: `pay_item.changed_by` — `auth.getName()`, ya'ni aktyor
  //    identifikatori (UUID yoki login). Nom `actorName.ts` xaritasidan.
  const actors = useActorDirectory()

  const [orgNodeId, setOrgNodeId] = useState<string | null>(null)

  const nodeSource = useEmployeeNodes(canView)

  const filter = useMemo(() => ({ orgNodeId }), [orgNodeId])

  if (!canView) {
    return (
      <>
        <PageHeader title={t('nav.hrSalaries')} description={t('pageIntro.hrSalaries')} />
        <Card>
          <PermissionNotice
            permission="hr.salary.view"
            title={t('hr.salary.hiddenTitle')}
            reason={t('hr.salary.hiddenReason')}
          />
        </Card>
      </>
    )
  }

  const columns: ColumnsType<PayItem> = [
    {
      // ⚠️ Ism javobdan keladi (`row.fullName`) — ro'yxat AYNAN shu
      // ustun bo'yicha saralangan, ya'ni ko'rinadigan tartib bilan
      // ko'rinadigan ism bitta manbadan.
      title: t('hr.salaryList.person'),
      key: 'person',
      render: (_value, row) => (
        <Typography.Link onClick={() => void navigate(`/hr/persons/${row.personId}`)}>
          {row.fullName ?? '—'}
        </Typography.Link>
      ),
    },
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.componentName ? seededName('payComponents', row.componentCode, row.componentName, t) : (row.componentCode ?? '—')}</span>
          <Tag color={row.kind === 'EARNING' ? 'green' : 'volcano'}>
            {t(`payComponentKind.${row.kind}`)}
          </Tag>
        </Space>
      ),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.amount),
    },
    {
      // ⚠️ `LocalDate` — XOM ko'rsatiladi. Zona konvertatsiyasi kunni
      // siljitardi (`SalaryCard` bilan bir xil qoida).
      title: t('hr.salary.validFrom'),
      dataIndex: 'validFrom',
      key: 'validFrom',
    },
    {
      title: t('hr.salary.reason'),
      key: 'reason',
      render: (_value, row) =>
        row.reason === null ? '—' : <ExpandableHint text={row.reason} />,
    },
    {
      title: t('hr.salary.changedBy'),
      key: 'changedBy',
      render: (_value, row) => actorLabel(row.changedBy, actors, t('common.dash')),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.hrSalaries')} description={t('pageIntro.hrSalaries')} />

      <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
        <ExpandableHint text={t('hr.salaryList.listHint')} fontSize={13} />

        {nodeSource.reason === 'ok' ? (
          <Tooltip title={t('hr.salaryList.filterNodeHint')}>
            <div style={{ width: 320 }}>
              <OrgNodeTreeSelect
                nodes={nodeSource.nodes}
                types={nodeSource.types}
                value={orgNodeId}
                onChange={setOrgNodeId}
                loading={nodeSource.loading}
                allowClear
                placeholder={t('hr.salaryList.filterNode')}
              />
            </div>
          </Tooltip>
        ) : null}
      </Space>

      <DataTable<PayItem>
        queryKey={[...queryKeys.hr.salaries, filter]}
        fetchPage={fetchSalaryPage(filter)}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="hr.salaries"
        emptyTitle={t('hr.salaryList.empty')}
        emptyDescription={t('hr.salaryList.emptyHint')}
      />
    </>
  )
}
